/**
 * "Hero" style tile. Designed for single item hero carousels.
 * Currently only used on the home page.
 */

import { forwardRef, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import offerPageURL from 'lib/offer/offerPageURL'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import {
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
  OFFER_TYPE_CRUISE,
  OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_VILLA,
} from 'constants/offer'

import { buildSearchParamsFromFilters, buildSuggestedDatesParamsKey } from 'lib/search/searchUtils'
import type { TopLevelTileProps } from '../OfferTileTypes'
import { connect } from 'react-redux'
import { getSuggestedDates } from 'selectors/offerSelectors'
import noop from 'lib/function/noop'
import { P, match } from 'ts-pattern'
import HeroHotelOfferTile from './HeroHotelOfferTile'
import HeroTourOfferTile from './HeroTourOfferTile'
import NestableClickable from 'components/Common/NestableClickable/NestableClickable'

import HeroCruiseOfferTile from './HeroCruiseOfferTile'
import HeroBedbankOfferTile from './HeroBedbankOfferTile'
import HeroBundleAndSaveOfferTile from './HeroBundleAndSaveOfferTile'
import HeroVillaOfferTile from './HeroVillaOfferTile'
import { isCruiseV1Offer } from 'lib/offer/offerTypes'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'

const OfferLink = styled(NestableClickable)`
  position: relative;
  display: block;
  height: 100%;

  // Sold out villas return null so need to be hidden
  &:empty {
    display: none;
  }
`

function getTestId(offer: App.AnyOffer) {
  let flightsEnabled: boolean = false
  let onlinePurchaseSchedule: App.OfferSchedule | undefined = undefined

  if (offer.type !== OFFER_TYPE_BED_BANK) {
    const internalOffer = offer as App.Offer

    flightsEnabled = internalOffer.offerFlightsEnabled
    onlinePurchaseSchedule = internalOffer.onlinePurchaseSchedule
  }

  return clsx(
    `offer-${offer.type}`, {
      'offer-with-flights': flightsEnabled,
      [`Purchasable-${offer.type}`]: scheduleIsCurrent(onlinePurchaseSchedule),
    })
}

interface Props extends TopLevelTileProps {
  className?: string
}

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates
}

const HeroOfferTile = forwardRef<HTMLAnchorElement, Props & MappedStateProps>((props, ref) => {
  const {
    offer,
    filters,
    productClick = noop,
    eagerLoadFirstImage,
    offerLinkIncludesFilters = true,
    suggestedDates,
    className,
  } = props

  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const offerUrl = useMemo(() => {
    const searchParams = offerLinkIncludesFilters ? buildSearchParamsFromFilters(filters, suggestedDates) : new URLSearchParams()
    return offerPageURL(offer, searchParams)
  }, [filters, offer, offerLinkIncludesFilters, suggestedDates])

  const handleClick = useCallback(() => {
    productClick(offer)
  }, [offer, productClick])

  const handleContextMenu = useCallback(() => {
    productClick(offer, true)
  }, [offer, productClick])

  const linkParams = useMemo(() => {
    return {
      onClick: handleClick,
      onContextMenu: handleContextMenu,
      to: offerUrl,
      'data-testid': getTestId(offer),
      ...(isOpenNewTabOfferClickEnabled ? { target: '_blank' } : {}),
    }
  }, [isOpenNewTabOfferClickEnabled, offerUrl, offer, handleClick, handleContextMenu])

  return (<OfferLink {...linkParams} className={className} ref={ref}>
    {match(offer)
      .with({
        parentType: P.union(
          OFFER_TYPE_HOTEL,
          OFFER_TYPE_TOUR,
        ),
      }, (item) => {
        if (item.type === OFFER_TYPE_BUNDLE_AND_SAVE) {
          return (
            <HeroBundleAndSaveOfferTile
              offer={item as App.BundleOffer | App.BundleOfferSummary}
              eagerLoadFirstImage={eagerLoadFirstImage}
              offerUrl={offerUrl}
            />
          )
        }

        if (item.type === OFFER_TYPE_BED_BANK) {
          return (
            <HeroBedbankOfferTile
              offer={item}
              eagerLoadFirstImage={eagerLoadFirstImage}
              offerUrl={offerUrl}
            />
          )
        }

        if (isCruiseV1Offer(item)) {
          return <HeroCruiseOfferTile
            offer={item}
            eagerLoadFirstImage={eagerLoadFirstImage}
            offerUrl={offerUrl}
          />
        }

        if (item.type === OFFER_TYPE_VILLA) {
          return <HeroVillaOfferTile
            offer={item as App.VillaOffer | App.VillaOfferSummary}
            eagerLoadFirstImage={eagerLoadFirstImage}
            offerUrl={offerUrl}
          />
        }

        return (
          <HeroHotelOfferTile
            offer={item}
            eagerLoadFirstImage={eagerLoadFirstImage}
            offerUrl={offerUrl}
          />
        )
      }).with({
        parentType: OFFER_TYPE_TOUR_V2,
      }, (item) => (
        <HeroTourOfferTile
          offer={item}
          eagerLoadFirstImage={eagerLoadFirstImage}
          offerUrl={offerUrl}
        />
      )).with({
        parentType: OFFER_TYPE_CRUISE,
      }, (item) => (
        <HeroCruiseOfferTile
          offer={item}
          eagerLoadFirstImage={eagerLoadFirstImage}
          offerUrl={offerUrl}
        />
      ))
      .otherwise((): null => null)}
  </OfferLink>
  )
})

export default connect((appState: App.State, ownProps: Props) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.filters?.flexibleMonths, ownProps.filters?.flexibleNights, ownProps.filters?.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
  }
})(HeroOfferTile)
